import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const EMPTY = {
    pickup: "", destination: "", date: "", time: "", passengers: 2, luggage_count: 0, luggage_type: "",
    desired_stops: "", duration: "", special_requests: "", language: "it",
    customer_name: "", customer_email: "", customer_phone: "", privacy_consent: false,
};

export default function TailorMade() {
    const { lang, t } = useLang();
    const [params] = useSearchParams();
    const [form, setForm] = useState(() => ({
        ...EMPTY,
        language: lang,
        pickup: params.get("pickup") || "",
        destination: params.get("destination") || "",
        date: params.get("date") || "",
        passengers: Number(params.get("passengers")) || 2,
    }));
    const [sending, setSending] = useState(false);
    const [code, setCode] = useState(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const valid = form.pickup.length >= 2 && form.destination.length >= 2 && form.date && form.time &&
        form.customer_name.length >= 2 && form.customer_email.includes("@") && form.customer_phone.length >= 5 && form.privacy_consent;

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const { data } = await api.post("/public/tailor-made", {
                ...form,
                passengers: Number(form.passengers),
                luggage_count: Number(form.luggage_count),
                luggage_type: form.luggage_type || null,
                desired_stops: form.desired_stops || null,
                duration: form.duration || null,
                special_requests: form.special_requests || null,
            });
            setCode(data.code);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(apiError(err, t.booking.errorGeneric));
        } finally {
            setSending(false);
        }
    };

    const L = ({ children }) => <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{children}</span>;

    return (
        <div data-testid="tailor-made-page" className="marble-bg min-h-screen pt-32 pb-24">
            <Seo lang={lang} title={`${t.tailor.title} — TOURVOYAGERS`} description={t.tailor.sub} />
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <Reveal>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium">TOURVOYAGERS · CONCIERGE</p>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl text-amber-50 leading-tight">{t.tailor.title}</h1>
                    <p className="mt-4 text-base text-stone-300 leading-relaxed">{t.tailor.sub}</p>
                </Reveal>

                {code ? (
                    <Reveal>
                        <div className="mt-12 gold-card rounded-xl p-10 text-center" data-testid="tailor-success">
                            <CheckCircle2 className="mx-auto text-amber-400" size={52} />
                            <h2 className="mt-4 font-display text-2xl text-amber-50">{t.tailor.successTitle}</h2>
                            <p className="mt-3 text-sm text-stone-300 leading-relaxed">{t.tailor.successMsg}</p>
                            <p className="mt-5 font-mono-accent text-xl text-amber-300" data-testid="tailor-success-code">{code}</p>
                        </div>
                    </Reveal>
                ) : (
                    <Reveal delay={0.1}>
                        <form onSubmit={submit} className="mt-12 gold-card rounded-xl p-8 grid grid-cols-2 gap-4" data-testid="tailor-form">
                            <label className="block col-span-2 sm:col-span-1"><L>{t.tailor.pickup} *</L><input data-testid="tailor-pickup" className="input-lux mt-1" value={form.pickup} onChange={(e) => set("pickup", e.target.value)} /></label>
                            <label className="block col-span-2 sm:col-span-1"><L>{t.tailor.destination} *</L><input data-testid="tailor-destination" className="input-lux mt-1" value={form.destination} onChange={(e) => set("destination", e.target.value)} /></label>
                            <label className="block"><L>{t.tailor.date} *</L><input data-testid="tailor-date" type="date" className="input-lux mt-1" value={form.date} onChange={(e) => set("date", e.target.value)} /></label>
                            <label className="block"><L>{t.tailor.time} *</L><input data-testid="tailor-time" type="time" className="input-lux mt-1" value={form.time} onChange={(e) => set("time", e.target.value)} /></label>
                            <label className="block"><L>{t.tailor.passengers} *</L><input data-testid="tailor-passengers" type="number" min="1" max="16" className="input-lux mt-1" value={form.passengers} onChange={(e) => set("passengers", e.target.value)} /></label>
                            <label className="block"><L>{t.tailor.luggageCount}</L><input data-testid="tailor-luggage-count" type="number" min="0" max="20" className="input-lux mt-1" value={form.luggage_count} onChange={(e) => set("luggage_count", e.target.value)} /></label>
                            <label className="block col-span-2"><L>{t.tailor.luggageType}</L><input data-testid="tailor-luggage-type" className="input-lux mt-1" value={form.luggage_type} onChange={(e) => set("luggage_type", e.target.value)} /></label>
                            <label className="block col-span-2"><L>{t.tailor.stops}</L><input data-testid="tailor-stops" className="input-lux mt-1" value={form.desired_stops} onChange={(e) => set("desired_stops", e.target.value)} /></label>
                            <label className="block col-span-2 sm:col-span-1"><L>{t.tailor.duration}</L><input data-testid="tailor-duration" className="input-lux mt-1" value={form.duration} onChange={(e) => set("duration", e.target.value)} /></label>
                            <label className="block col-span-2 sm:col-span-1"><L>{t.tailor.language}</L>
                                <select data-testid="tailor-language" className="input-lux mt-1" value={form.language} onChange={(e) => set("language", e.target.value)}>
                                    <option value="it">{t.tailor.langIt}</option>
                                    <option value="en">{t.tailor.langEn}</option>
                                </select>
                            </label>
                            <label className="block col-span-2"><L>{t.tailor.requests}</L><textarea data-testid="tailor-requests" rows={3} className="input-lux mt-1" value={form.special_requests} onChange={(e) => set("special_requests", e.target.value)} /></label>
                            <label className="block col-span-2"><L>{t.tailor.name} *</L><input data-testid="tailor-name" className="input-lux mt-1" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></label>
                            <label className="block col-span-2 sm:col-span-1"><L>{t.tailor.email} *</L><input data-testid="tailor-email" type="email" className="input-lux mt-1" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></label>
                            <label className="block col-span-2 sm:col-span-1"><L>{t.tailor.phone} *</L><input data-testid="tailor-phone" type="tel" className="input-lux mt-1" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></label>
                            <label className="col-span-2 flex items-start gap-3 text-xs text-stone-400 cursor-pointer">
                                <input data-testid="tailor-privacy" type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-0.5 accent-amber-400" />
                                {t.tailor.privacy}
                            </label>
                            <div className="col-span-2 pt-2">
                                <button data-testid="tailor-submit" disabled={!valid || sending} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-4 text-xs font-bold uppercase tracking-[0.16em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.45)] disabled:opacity-40 disabled:cursor-not-allowed">
                                    {sending && <Loader2 size={14} className="animate-spin" />}
                                    {t.tailor.submit}
                                </button>
                            </div>
                        </form>
                    </Reveal>
                )}
            </div>
        </div>
    );
}
